import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { useRef, useState } from 'react';
import UsersAutocomplete from '../../../shared/users-autocomplete';
import GroupsAutocomplete from '../../../shared/groups-autocomplete';
import DomainsAutoComplete from '../../../shared/domain-autocomplete';
import { PolicyAction, PolicyFormValues } from '../policy-dialog';
import z from 'zod';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Delete from '@mui/icons-material/Delete';
interface RemoveEmailDelegateActionProps {
  setValue: UseFormSetValue<PolicyFormValues>;
  control: Control<PolicyFormValues>;
  watch: UseFormWatch<PolicyFormValues>;
  errors: FieldErrors<PolicyFormValues>;
  trigger: UseFormTrigger<PolicyFormValues>;
  domains: string[];
}

export const RemoveEmailDelegateActionSchema = z
  .object({
    type: z.literal('Remove_Delegate_From_Account'),
    removeDelegateFromAccount: z.array(z.string()).nullable(),
    removeAllDelegatesEnabled: z.boolean(),
    valueType: z.string(),
  })
  .superRefine((data, ctx) => {
    if (
      data.removeAllDelegatesEnabled !== true &&
      (!data.removeDelegateFromAccount ||
        data.removeDelegateFromAccount.length === 0)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['removeDelegateFromAccount'],
        message: 'Please select a group or all groups to remove.',
      });
    }
  });

export type RemoveEmailDelegateActionType = z.infer<
  typeof RemoveEmailDelegateActionSchema
>;

// Type guard to check if an action is RemoveEmailDelegateActionType
function isRemoveEmailDelegateAction(
  action: PolicyAction
): action is RemoveEmailDelegateActionType {
  return action.type === 'Remove_Delegate_From_Account';
}

export default function RemoveEmailDelegateAction({
  setValue,
  watch,
  errors,
  domains,
  control,
  trigger,
}: RemoveEmailDelegateActionProps) {
  const actionData = watch('actionData');
  const [autoCompleteResponse, setAutoCompleteResponse] = useState('');
  const [alreadySelectedError, setAlreadySelectedError] = useState(false);
  const autocompleteKey = useRef(new Date().getTime().toString());
  const idx = actionData
    .map((action) => action.type)
    .indexOf('Remove_Delegate_From_Account');
  const actionAtIndex = actionData[idx];
  if (!isRemoveEmailDelegateAction(actionAtIndex)) {
    return null;
  }

  const currentAction: RemoveEmailDelegateActionType = actionAtIndex;
  const error: FieldErrors<RemoveEmailDelegateActionType> | undefined =
    errors?.actionData?.[idx];

  function addSelection(delegate: string) {
    if (currentAction.removeDelegateFromAccount?.includes(delegate)) {
      setAlreadySelectedError(true);
    } else {
      setAlreadySelectedError(false);
      const value = currentAction.removeDelegateFromAccount
        ? [...currentAction.removeDelegateFromAccount, delegate]
        : [delegate];
      setValue(`actionData.${idx}.removeDelegateFromAccount`, value, {
        shouldDirty: true,
        shouldValidate: true,
      });
      trigger(`actionData.${idx}.removeDelegateFromAccount`);
    }

    autocompleteKey.current = new Date().getTime().toString();
    setAutoCompleteResponse('');
  }

  function removeSelection(email: string) {
    const newList = currentAction.removeDelegateFromAccount?.filter(
      (userEmail) => userEmail !== email
    );
    setValue(
      `actionData.${idx}.removeDelegateFromAccount`,
      newList ? [...newList] : []
    );
  }

  return (
    <Stack sx={{ p: 1 }}>
      <Stack gap={1}>
        <Stack flexDirection={'row'} gap={4} alignItems={'center'}>
          <FormControl>
            <FormLabel
              sx={{
                fontWeight: 'bold',
                color: (theme) => theme.palette.text.primary,
              }}
            >
              {currentAction.removeDelegateFromAccount &&
              currentAction.removeDelegateFromAccount.length > 0
                ? 'Delegate Type (Only one target type is allowed)'
                : 'Delegate Type'}
            </FormLabel>
            <RadioGroup
              aria-labelledby="radio-buttons-group-label"
              defaultValue="User"
              name="radio-buttons-group"
              value={currentAction.valueType}
              onChange={(event) =>
                setValue(`actionData.${idx}.valueType`, event.target.value)
              }
              row
            >
              <FormControlLabel
                value="User"
                control={<Radio size="small" />}
                label="User"
                disabled={
                  currentAction.removeAllDelegatesEnabled ||
                  (currentAction.removeDelegateFromAccount &&
                    currentAction.removeDelegateFromAccount.length > 0)
                    ? true
                    : false
                }
              />
              <FormControlLabel
                value="Group"
                control={<Radio size="small" />}
                label="Group"
                disabled={
                  currentAction.removeAllDelegatesEnabled ||
                  (currentAction.removeDelegateFromAccount &&
                    currentAction.removeDelegateFromAccount.length > 0)
                    ? true
                    : false
                }
              />
            </RadioGroup>
          </FormControl>
        </Stack>
        <Stack flexDirection={'row'} gap={1}>
          {currentAction.valueType === 'User' ? (
            <Box sx={{ width: 400 }}>
              <Controller
                rules={{
                  required: !currentAction.removeAllDelegatesEnabled
                    ? 'Delegate is required.'
                    : false,
                }}
                name={`actionData.${idx}.removeDelegateFromAccount`}
                control={control}
                render={() => (
                  <UsersAutocomplete
                    newKey={`${autocompleteKey.current}`}
                    autocompleteValue={autoCompleteResponse}
                    setAutoCompleteValue={(value) => {
                      if (!value) {
                        setAlreadySelectedError(false);
                        return;
                      }
                      addSelection(value);
                    }}
                    disabled={currentAction.removeAllDelegatesEnabled}
                    errors={
                      !!error?.removeDelegateFromAccount || alreadySelectedError
                    }
                    helperText={
                      alreadySelectedError
                        ? 'Delegate is already selected.'
                        : error?.removeDelegateFromAccount
                        ? error?.removeDelegateFromAccount?.message
                        : ' '
                    }
                  />
                )}
              />
            </Box>
          ) : currentAction.valueType === 'Group' ? (
            <Box sx={{ width: 400 }}>
              <Controller
                name={`actionData.${idx}.removeDelegateFromAccount`}
                control={control}
                render={() => (
                  <GroupsAutocomplete
                    newKey={`${autocompleteKey.current}`}
                    autocompleteValue={autoCompleteResponse}
                    setAutoCompleteValue={(value) => {
                      if (!value) {
                        setAlreadySelectedError(false);
                        return;
                      }
                      addSelection(value);
                    }}
                    disabled={currentAction.removeAllDelegatesEnabled}
                    errors={
                      !!error?.removeDelegateFromAccount || alreadySelectedError
                    }
                    helperText={
                      alreadySelectedError
                        ? 'Delegate is already selected.'
                        : error?.removeDelegateFromAccount
                        ? error?.removeDelegateFromAccount?.message
                        : ' '
                    }
                  />
                )}
              />
            </Box>
          ) : (
            <Box sx={{ width: 400 }}>
              <Controller
                name={`actionData.${idx}.removeDelegateFromAccount`}
                control={control}
                render={() => (
                  <DomainsAutoComplete
                    newKey={`${autocompleteKey.current}`}
                    size="medium"
                    domains={domains}
                    setAutoCompleteValue={(value) => {
                      if (!value) {
                        setAlreadySelectedError(false);
                        return;
                      }
                      addSelection(value);
                    }}
                    disabled={currentAction.removeAllDelegatesEnabled}
                    hasError={
                      !!error?.removeDelegateFromAccount || alreadySelectedError
                    }
                    helperText={
                      alreadySelectedError
                        ? 'Delegate is already selected.'
                        : error?.removeDelegateFromAccount
                        ? error?.removeDelegateFromAccount?.message
                        : ' '
                    }
                  />
                )}
              />
            </Box>
          )}
        </Stack>
      </Stack>
      <Stack flexDirection={'row'} alignItems={'center'}>
        <Controller
          name={`actionData.${idx}.removeAllDelegatesEnabled`}
          control={control}
          render={({ field: props }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...props}
                  checked={props.value}
                  onChange={(e) => {
                    props.onChange(e.target.checked);
                    setValue(`actionData.${idx}.removeDelegateFromAccount`, []);
                  }}
                />
              }
              sx={{ width: 'fit-content' }}
              label="Remove All Delegates"
            />
          )}
        />
      </Stack>
      <Box sx={{ maxHeight: 185, mt: 1 }}>
        <Paper sx={{ minWidth: 470, overflow: 'hidden', maxHeight: 185 }}>
          <TableContainer sx={{ minWidth: 470, maxHeight: 185 }}>
            <Table size={'small'} stickyHeader aria-label="searched-delegates">
              <TableHead>
                <TableRow>
                  <TableCell>Selected Delegates</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentAction.removeDelegateFromAccount
                  ? currentAction.removeDelegateFromAccount.map((user) => (
                      <TableRow key={user}>
                        <TableCell>
                          <Stack
                            flexDirection="row"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Typography variant="body2">{user}</Typography>
                            <Tooltip arrow title="Delete">
                              <span>
                                <IconButton
                                  onClick={() => {
                                    removeSelection(user);
                                  }}
                                  size="small"
                                >
                                  <Delete />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Stack>
  );
}
